import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChefHat, Loader2 } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", restaurant_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { k: "restaurant_name", label: "Restaurant name", type: "text", ph: "The Copper Spoon", testid: "register-restaurant-input" },
    { k: "name", label: "Your name", type: "text", ph: "Alex Carter", testid: "register-name-input" },
    { k: "email", label: "Email", type: "email", ph: "you@restaurant.com", testid: "register-email-input" },
    { k: "password", label: "Password", type: "password", ph: "min. 6 characters", testid: "register-password-input" },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-xl bg-orange-600 grid place-items-center">
            <ChefHat className="h-6 w-6 text-white" />
          </div>
          <span className="font-sub font-bold text-2xl">QRFoody</span>
        </Link>
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
          <h1 className="font-heading text-3xl font-bold">Create your restaurant</h1>
          <p className="text-slate-400 text-sm mt-1">Get your QR menu live in minutes.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {fields.map((f) => (
              <div key={f.k}>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-400">{f.label}</label>
                <input
                  data-testid={f.testid}
                  type={f.type}
                  required
                  value={form[f.k]}
                  onChange={set(f.k)}
                  placeholder={f.ph}
                  className="mt-1.5 w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>
            ))}
            {error && (
              <p data-testid="register-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <button
              data-testid="register-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 transition-colors rounded-xl py-3 font-semibold flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
            </button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            Already have an account?{" "}
            <Link data-testid="goto-login-link" to="/login" className="text-orange-400 font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
